import { useEffect, useState } from 'react';
import { ApiError, api, type GuestPassState } from '../api';

/*
 * Страница ссылки из письма (спека 2026-10-06-mail-invites-design.md): вне
 * оболочки, как страница договора гостя, и открывается чаще всего с
 * телефона.
 *
 * Живую ссылку гасит только нажатие «Открыть документ» (решение Ильи
 * 07.10.2026): почтовые сканеры открывают ссылки из писем GET-ом и даже
 * исполняют скрипты страницы, поэтому вход при загрузке сжигал бы
 * одноразовую ссылку раньше человека. Страница лишь спрашивает, жива ли
 * ссылка, а входит клик живого человека. Использованная или истёкшая —
 * новая ссылка уходит только на ту же почту, страница называет её
 * наполовину, чтобы человек узнал свою.
 */
export function GuestPass({ pass, go }: { pass: string; go: (url: string) => void }) {
  const [st, setSt] = useState<GuestPassState | null>(null);
  const [sentTo, setSentTo] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .guestPass(pass)
      .then((s) => {
        if (alive) setSt(s);
      })
      .catch((e) => {
        if (!alive) return;
        if (e instanceof ApiError && e.status === 404) setSt({ state: 'DEAD', email: '' });
        else setErr('Не удалось открыть ссылку: проверьте связь');
      });
    return () => {
      alive = false;
    };
  }, [pass]);

  const enter = () => {
    setBusy(true);
    setErr('');
    api
      .guestPassEnter(pass)
      .then(({ url }) => go(url))
      .catch((e) => {
        // Кнопка оживает только при отказе: после успеха страница живёт до
        // перехода, и второе нажатие получило бы 409 и вспыхнуло бы «уже использована».
        setBusy(false);
        // Отказ сервера называется своими словами: пока страница лежала открытой,
        // ссылку могли открыть на другом устройстве или у неё вышел срок.
        // Обрыв связи о ссылке ничего не говорит — «использована» было бы
        // враньём, как и «недействительна» на странице договора.
        const email = st?.email ?? '';
        if (e instanceof ApiError) {
          if (e.status === 404) setSt({ state: 'DEAD', email: '' });
          else if (e.status === 409 && e.message === 'Срок ссылки истёк') setSt({ state: 'EXPIRED', email });
          else setSt({ state: 'USED', email });
        } else setErr('Не удалось открыть документ: проверьте связь');
      });
  };

  const askNew = () => {
    setBusy(true);
    setErr('');
    api
      .guestPassNew(pass)
      .then((r) => setSentTo(r.email))
      .catch((e) => setErr(e instanceof ApiError ? e.message : 'Не удалось отправить ссылку: проверьте связь'))
      .finally(() => setBusy(false));
  };

  const head =
    st?.state === 'USED' ? 'Ссылка уже использована' : st?.state === 'EXPIRED' ? 'Срок ссылки истёк' : 'Ссылка недействительна';

  return (
    <div className="app">
      <div className="wrap">
        <div className="nav-page">
          {!st && !err && <div className="load-screen">Проверяю ссылку…</div>}
          {st?.state === 'LIVE' && (
            <div className="empty">
              <b>Приглашение к документу</b>
              Нажмите, чтобы открыть документ. Ссылка срабатывает один раз.
              <button className="btn primary" type="button" disabled={busy} onClick={enter}>
                Открыть документ
              </button>
            </div>
          )}
          {st && st.state !== 'LIVE' && (
            <div className="empty">
              <b>{head}</b>
              {st.state === 'DEAD'
                ? 'Возможно, пришло письмо с новой ссылкой — откройте последнее. Если его нет, попросите сотрудника CODE прислать новое приглашение.'
                : sentTo
                  ? `Новая ссылка отправлена на ${sentTo}. Проверьте почту.`
                  : `Ссылка срабатывает один раз. Новая придёт на ${st.email}.`}
              {st.state !== 'DEAD' && !sentTo && (
                <button className="btn primary" type="button" disabled={busy} onClick={askNew}>
                  Прислать новую ссылку
                </button>
              )}
            </div>
          )}
          {!!err && (
            <div className="banner crit">
              <b>{err}</b>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
